import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useSystem, SYSTEM_META, SERVICE_META } from "@/lib/system";
import { StatCard, Panel, Badge, StatusDot } from "@/components/Bits";
import { fmtCompact, PRESENCE_DOT, STATUS_META } from "@/lib/format";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { AlertTriangle, Activity, Clock, CheckCircle2 } from "lucide-react";

export default function Dashboard() {
  const { system } = useSystem();
  const [s, setS] = useState(null);
  const [presence, setPresence] = useState([]);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/dashboard/stats", { params: system ? { system } : {} }),
      api.get("/dashboard/presence"),
    ]);
    setS(r1.data);
    setPresence(r2.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [system]);

  if (!s) return <div className="p-8 text-slate-400" data-testid="dashboard-loading">Loading dashboard...</div>;

  const statusData = Object.entries(s.by_status).map(([k, v]) => ({ name: STATUS_META[k]?.label || k, value: v, key: k }));
  const systemLabel = system ? SYSTEM_META[system]?.label : "All Systems";

  return (
    <div className="p-6 space-y-5 animate-slide-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Operations Overview</div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" />
          Live · refresh 15s
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="kpi-open" label="Open Tickets" value={s.open} sub={`${s.total} total`} accent="text-cyan" />
        <StatCard testid="kpi-breaches" label="SLA Breaches" value={s.breaches} sub="Bank/Response" accent={s.breaches > 0 ? "text-rose-400" : "text-emerald-400"} />
        <StatCard testid="kpi-avg-resolution" label="Avg Resolution" value={fmtCompact(s.avg_resolution_sec)} sub="Created → Resolved" accent="text-white" />
        <StatCard testid="kpi-avg-bank" label="Avg Bank Response" value={fmtCompact(s.avg_bank_response_sec)} sub={`Longest: ${fmtCompact(s.longest_bank_wait_sec)}`} accent="text-violet-400" />
      </div>

      {/* Service breakdown row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="service-breakdown">
        {Object.entries(s.by_service || {}).map(([k, v]) => (
          <div key={k} className="bg-surface border border-line rounded-sm p-3" data-testid={`service-${k}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{SERVICE_META[k]?.label || k}</span>
              <Badge color={SERVICE_META[k]?.color}>{SERVICE_META[k]?.short}</Badge>
            </div>
            <div className="text-2xl font-mono font-bold text-cyan tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Last 7 Days · Tickets Created" className="lg:col-span-2" testid="chart-7d">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={s.recent_7d}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#263346" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#131B2A", border: "1px solid #263346", borderRadius: 2, fontSize: 12 }}
                  labelStyle={{ color: "#94a3b8" }}
                />
                <Area type="monotone" dataKey="count" stroke="#00E5FF" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="By Status" testid="chart-status">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical" margin={{ left: 60 }}>
                <XAxis type="number" stroke="#64748b" fontSize={10} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={10} width={120} />
                <Tooltip contentStyle={{ background: "#131B2A", border: "1px solid #263346", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="value" fill="#00E5FF" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* Bottom row: breakdown + presence */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="By System" testid="panel-system">
          <div className="space-y-2">
            {Object.entries(s.by_system || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 border border-line rounded-sm">
                <div className="flex items-center gap-2">
                  <Badge color={SYSTEM_META[k]?.color}>{SYSTEM_META[k]?.short || k}</Badge>
                  <span className="text-sm">{SYSTEM_META[k]?.label || k}</span>
                </div>
                <span className="font-mono text-lg text-cyan">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-line space-y-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Customer Type ({systemLabel})</div>
            {Object.entries(s.by_customer).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{k}</span>
                <span className="font-mono text-white">{v}</span>
              </div>
            ))}
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 pt-1">Transaction Type</div>
            {Object.entries(s.by_txn).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{k}</span>
                <span className="font-mono text-white">{v}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title={`Top Merchants${system ? ` · ${systemLabel}` : ""}`} testid="panel-top-merchants">
          <div className="space-y-1.5">
            {(!s.top_merchants || s.top_merchants.length === 0) && <div className="text-xs text-slate-500">No merchants yet.</div>}
            {(s.top_merchants || []).map((m, i) => (
              <div key={m.merchant} className="flex items-center justify-between px-2 py-1.5 hover:bg-surface2 rounded-sm" data-testid={`top-merchant-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-mono text-slate-500 w-5">#{i + 1}</span>
                  <span className="text-xs truncate">{m.merchant}</span>
                </div>
                <div className="flex items-center gap-2 text-[10px] tabular-nums">
                  <span className="font-mono text-cyan">{m.count}</span>
                  <span className="text-slate-500">({m.open} open)</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Team Presence" testid="panel-presence">
          <div className="space-y-2">
            {presence.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-3 py-2 border border-line/50 rounded-sm">
                <div className="flex items-center gap-2.5">
                  <span className={`w-2 h-2 rounded-full ${PRESENCE_DOT[u.presence]} ${u.presence === "online" ? "pulse-dot" : ""}`} />
                  <div>
                    <div className="text-xs font-medium">{u.name}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{u.presence} · {u.role}</div>
                  </div>
                </div>
                <span className="text-[10px] text-slate-500 font-mono">{u.open_tickets} open</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Agent workload row */}
      <div className="grid grid-cols-1 gap-4">
        <Panel title="Agent Workload" testid="panel-workload">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {s.by_agent.length === 0 && <div className="text-xs text-slate-500">No agents.</div>}
            {s.by_agent.map((row) => (
              <div key={row.agent.id} className="flex items-center justify-between px-3 py-2 hover:bg-surface2 rounded-sm border border-line">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 rounded-sm bg-surface2 border border-line flex items-center justify-center text-xs font-bold text-cyan shrink-0">
                    {row.agent.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{row.agent.name}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{row.agent.role}</div>
                  </div>
                </div>
                <span className="font-mono text-sm text-cyan tabular-nums shrink-0">{row.open_tickets}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
